import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Sheet,
  Typography,
} from '@mui/joy';
import LoginIcon from '@mui/icons-material/Login';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from '../../main/context/AuthContext';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { getPublicPaySiteDisplayName } from '../../main/utils/siteDisplayName';
import { resolvePublicInvoicePay } from '../../main/API/publicPay';
import PublicBrandedPageShell from './PublicBrandedPageShell';

export default function PublicInvoicePayScreen() {
  const { t, i18n } = useTranslation('ui');
  const { token } = useParams();
  const { settings } = useSiteSettings();
  const { isAuthenticated } = useAuth();
  const siteName = getPublicPaySiteDisplayName(settings, i18n.language);
  const siteIcon = settings?.site_icon && String(settings.site_icon).trim();
  const [message, setMessage] = useState(null);
  const [messageTone, setMessageTone] = useState('danger');
  const [redirecting, setRedirecting] = useState(true);

  const resolvePayLink = useCallback(async () => {
    setRedirecting(true);
    setMessage(null);
    setMessageTone('danger');
    try {
      const res = await resolvePublicInvoicePay(token);
      const paymentLink = res?.data?.payment_link;
      if (!paymentLink) {
        setMessage(t('publicInvoicePay.loadFailed'));
        setRedirecting(false);
        return;
      }
      window.location.href = paymentLink;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        setMessage(t('publicInvoicePay.notFound'));
      } else if (status === 410) {
        setMessage(t('publicInvoicePay.alreadyPaid'));
        setMessageTone('success');
      } else if (status === 502) {
        setMessage(t('publicInvoicePay.linkUnavailable'));
      } else {
        setMessage(t('publicInvoicePay.loadFailed'));
      }
      setRedirecting(false);
    }
  }, [token, t]);

  useEffect(() => {
    if (token) {
      resolvePayLink();
    }
  }, [token, resolvePayLink]);

  return (
    <PublicBrandedPageShell
      siteIcon={siteIcon}
      siteName={siteName}
      pageTitle={t('publicInvoicePay.pageTitle')}
      footer={
        !redirecting ? (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to={isAuthenticated ? '/customer-home' : '/customer-login'}
              size="lg"
              variant="soft"
              color="primary"
              startDecorator={isAuthenticated ? <HomeIcon /> : <LoginIcon />}
              sx={{ minWidth: 220 }}
            >
              {isAuthenticated ? t('nav.home') : t('auth.customerLogin')}
            </Button>
            {!isAuthenticated ? (
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1.5 }}>
                {t('publicInvoicePay.customerLoginHint')}
              </Typography>
            ) : null}
          </Box>
        ) : null
      }
    >
      <Sheet variant="outlined" sx={{ borderRadius: '14px', p: { xs: 2, sm: 3 } }}>
        {redirecting && !message && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size="lg" sx={{ mb: 2 }} />
            <Typography level="body-md">{t('publicInvoicePay.redirecting')}</Typography>
          </Box>
        )}
        {message && (
          <Alert color={messageTone} variant="soft">
            {message}
          </Alert>
        )}
      </Sheet>
    </PublicBrandedPageShell>
  );
}
